import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import MovieCard from '@/components/base/MovieCard';
import { movies } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function MovieDetail() {
  const { id } = useParams<{ id: string }>();
  const [movie, setMovie] = useState<Movie | null>(null);
  const relatedMovies = movies.filter(m => m.id !== id).slice(0, 6);

  useEffect(() => {
    const found = movies.find(m => m.id === id);
    setMovie(found || null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!movie) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-error-warning-line text-2xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-500 text-sm">电影未找到</p>
            <Link to="/" className="text-primary-500 text-sm mt-2 inline-block cursor-pointer hover:text-primary-600">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    image: movie.coverImage,
    dateCreated: `${movie.year}`,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.cast.map((name) => ({
      '@type': 'Person',
      name,
    })),
    contentRating: movie.rating,
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: typeof window !== 'undefined' ? window.location.origin + '/' : '',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '电影详情',
        item: typeof window !== 'undefined' ? window.location.href : '',
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${movie.title} - 在线观看 - 桃子影院`}
        description={`${movie.title}(${movie.year})完整版在线观看，${movie.description}导演：${movie.director}，主演：${movie.cast.join('、')}。高清${movie.quality}画质，永久免费不缓冲秒开播放。`}
        keywords={`${movie.title},在线观看,${movie.subcategory},桃子影院,${movie.director},${movie.cast.join(',')},免费电影`}
        canonicalPath={`/movie/${id}`}
        ogImage={movie.coverImage}
        ogType="video.movie"
        jsonLd={{ ...jsonLd, ...breadcrumbLd }}
      />

      <Navbar />

      <main className="pt-20">
        <Breadcrumb />

        <article className="w-full px-4 md:px-6 py-6">
          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="flex-shrink-0 w-full sm:w-48 md:w-56 lg:w-64">
              <div className="aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={movie.coverImage}
                  alt={`${movie.title} 电影海报`}
                  title={`${movie.title} - 桃子影院高清在线观看`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                <strong>{movie.title}</strong>
                <span className="text-lg font-normal text-foreground-500 ml-2">({movie.year})</span>
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                  {movie.quality}
                </span>
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap">
                  {movie.subcategory}
                </span>
                {movie.isNew && (
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700 whitespace-nowrap">
                    最新上映
                  </span>
                )}
                <span className="flex items-center gap-1 text-sm">
                  <i className="ri-star-fill text-accent-500 text-sm"></i>
                  <span className="font-bold text-foreground-900">{movie.rating}</span>
                </span>
              </div>

              <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm mb-5">
                <div className="flex items-baseline gap-2">
                  <span className="text-foreground-500 whitespace-nowrap">导演：</span>
                  <span className="text-foreground-800">{movie.director}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-foreground-500 whitespace-nowrap">地区：</span>
                  <span className="text-foreground-800">{movie.region}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-foreground-500 whitespace-nowrap">时长：</span>
                  <span className="text-foreground-800">{movie.duration}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-foreground-500 whitespace-nowrap">年份：</span>
                  <span className="text-foreground-800">{movie.year}</span>
                </div>
              </div>

              <div className="mb-5">
                <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2">主演</h2>
                <div className="flex flex-wrap gap-2">
                  {movie.cast.map((actor) => (
                    <span
                      key={actor}
                      className="px-3 py-1 rounded-md text-xs bg-background-100 text-foreground-600 whitespace-nowrap"
                    >
                      {actor}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2">剧情简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {movie.longDescription}
                </p>
              </div>
            </div>
          </div>
        </article>

        <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-100">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">
            推荐影片
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
            {relatedMovies.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}